'use client';

import React, { useState, useEffect, useRef } from 'react';
import pricingData from '../../../config/pricing_currencies.json';
import featuresData from '../../../config/pricing_body.json';
import CurrencySelector from './CurrencySelector';

// --- Types --- 
interface Plan {
  name: string;
  display_name: string;
  button_text: string;
  monthly_price: number | string;
  annual_price: number | string;
  stripe_links: {
    monthly: string;
    annual: string;
  }
}

// Interface to define keys that must be present in feature.included
type PlanName = 'Basic' | 'Pro' | 'Pro+' | 'Enterprise';

interface Feature {
  id: string;
  name: string;
  category: string;
  included: Record<PlanName, boolean | string>;
}

interface FeatureCategoryData {
  name: string;
  features: Feature[]; 
}

type CurrencyCode = 'CAD' | 'USD';
type PricingPlans = Record<CurrencyCode, Plan[]>;

type BillingPeriod = 'monthly' | 'annual';

// Interface for UI text elements
interface UiText {
  button_text: string;
  period_labels: {
    monthly: string;
    annual: string;
  };
  savings_text: string;
}

// --- Main Pricing Component ---
const PricingPlan: React.FC = () => {
  const [period, setPeriod] = useState<BillingPeriod>('annual');
  const [activePlan, setActivePlan] = useState<string | null>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [isWithinPricingSection, setIsWithinPricingSection] = useState(true);
  const [currency, setCurrency] = useState<CurrencyCode>(() => {
    // Find default currency from config
    const defaultCurrency = pricingData.currencies.find(curr => curr.default);
    return (defaultCurrency?.code as CurrencyCode) || 'CAD';
  });
  
  // Get current currency symbol
  const getCurrencySymbol = () => {
    const currencyObj = pricingData.currencies.find(curr => curr.code === currency);
    return currencyObj?.symbol || '$';
  };
  
  // Get plans based on current currency
  const getPlans = (): Plan[] => {
    return (pricingData.plans as PricingPlans)[currency] || [];
  };
  
  // Get UI text
  const getUiText = (): UiText => {
    return pricingData.ui_text as UiText;
  };
  
  const featureCategories: FeatureCategoryData[] = featuresData.featureCategories;
  
  // Refs for elements and positions
  const headerRef = useRef<HTMLDivElement>(null);
  const pricingSectionRef = useRef<HTMLDivElement>(null);
  const headerObserverRef = useRef<IntersectionObserver | null>(null);
  const headerHeight = useRef<number>(0);
  const sectionBottom = useRef<number>(0);

  // Handle currency change
  const handleCurrencyChange = (newCurrency: CurrencyCode) => {
    setCurrency(newCurrency);
  };

  // Initialize intersection observer for sticky header
  useEffect(() => {
    if (!headerRef.current) return;
    
    // Store original header height for spacer
    headerHeight.current = headerRef.current.offsetHeight;
    
    // Create new observer to watch when header leaves viewport
    headerObserverRef.current = new IntersectionObserver(
      ([entry]) => {
        // Header is sticky when it's not intersecting (scrolled out of view)
        setIsHeaderSticky(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-100px 0px 0px 0px' }
    );
    
    // Start observing the header element
    headerObserverRef.current.observe(headerRef.current);
    
    // Cleanup observer on unmount
    return () => {
      if (headerObserverRef.current) {
        headerObserverRef.current.disconnect();
      }
    };
  }, []);

  // Calculate pricing section boundaries and track scroll position
  useEffect(() => {
    // Skip if the pricing section ref isn't available yet
    if (!pricingSectionRef.current) return;

    // Function to calculate section boundaries
    const calculateBoundaries = () => {
      const rect = pricingSectionRef.current?.getBoundingClientRect();
      if (rect) {
        // Store the bottom position of the pricing section relative to the document
        sectionBottom.current = rect.bottom + window.scrollY;
      }
    };

    // Calculate once on mount
    calculateBoundaries();

    // Function to check if we're scrolled past the pricing section
    const handleScroll = () => {
      const viewportHeight = window.innerHeight;
      
      // Instead of showing until after passing the section,
      // hide the sticky header when approaching the end of the section
      // (when the end is visible and within the bottom 5% of the viewport)
      const endPosition = sectionBottom.current;
      const distanceFromBottom = endPosition - (window.scrollY + viewportHeight);
      
      if (distanceFromBottom < viewportHeight * 0.001) {
        // The end of the pricing section is visible and within the bottom 5% of viewport
        setIsWithinPricingSection(false);
      } else {
        setIsWithinPricingSection(true);
      }
    };

    // Handle resize to recalculate boundaries
    const handleResize = () => {
      calculateBoundaries();
      handleScroll(); // Check position after recalculating
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Update header height on resize to ensure spacer is correct size
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        headerHeight.current = headerRef.current.offsetHeight;
      }
    };

    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  // Function to set the billing period
  const setPeriodValue = (newPeriod: BillingPeriod) => {
    setPeriod(newPeriod);
  };

  // Function to just highlight a plan without redirecting
  const handleSelectPlan = (planName: string) => {
    setActivePlan(planName === activePlan ? null : planName);
  };

  // Handler for Select button click - redirects to URL
  const handleSelectButtonClick = (e: React.MouseEvent, plan: Plan) => {
    e.stopPropagation(); // Prevent column click from triggering
    
    // Highlight the plan
    setActivePlan(plan.name);
    
    // Determine which URL to use based on the current billing period
    const checkoutUrl = period === 'annual' 
      ? plan.stripe_links.annual 
      : plan.stripe_links.monthly;
    
    // Open the link in a new tab
    window.open(checkoutUrl, '_blank');
  };

  // Helper function to render feature value (checkmark, dash, or text)
  const renderFeatureValue = (value: boolean | string, isMobile: boolean = false) => {
    if (typeof value === 'boolean') {
      if (value) {
        return (
          // Use bold green checkmark
          <span className={`font-bold text-green-500 ${isMobile ? 'text-lg' : 'text-xl'}`}>
            ✓
          </span>
        );
      } else {
        return (
          // Use a simple dash for false values
          <span className={`text-gray-500 ${isMobile ? 'text-lg' : 'text-xl'}`}>
            -
          </span>
        );
      }
    }
    // Return text value as is
    return <span>{value}</span>;
  };

  // Helper function to format price with currency symbol
  const formatPrice = (planName: string, priceValue: number | string) => {
    if (planName === 'Enterprise' || priceValue === 'Custom') {
      return 'Custom';
    }
    
    // Get the correct symbol for the current currency
    const symbol = getCurrencySymbol();
    
    // Format the price with the symbol
    if (typeof priceValue === 'number') {
      return `${symbol}${priceValue}`;
    }
    
    return priceValue;
  };

  // Show sticky header only when within the pricing section and the original header is out of view
  const showStickyHeader = isHeaderSticky && isWithinPricingSection;
  const plans = getPlans();
  const uiText = getUiText();

  return (
    <div className="flex flex-col items-center w-full max-w-7xl mx-auto px-0 lg:px-8 text-white" ref={pricingSectionRef}>
      {/* Page Title */}
      <div className="text-center mb-12 w-full">
        <h1 className="text-3xl lg:text-4xl font-bold mb-4">Choose the Right Plan for Your Team</h1>
        <p className="text-lg lg:text-xl text-gray-300">MeetMinutes offers flexible plans to suit teams of all sizes</p>
      </div>
      
      {/* Pricing Header - Single Implementation with Glass Effect */}
      <div 
        ref={headerRef}
        className="w-full"
      >
        {/* Pricing Header Container */}
        <div className={`relative w-full bg-[#181818] bg-opacity-30 backdrop-blur-md rounded-t-md border border-white/10 transition-all duration-300 ${isHeaderSticky ? 'shadow-lg' : ''}`}>
          {/* Plan Selection Table - Fixed layout for consistent widths */}
          <div className="w-full overflow-x-auto">
            <table className="w-full table-fixed border-collapse">
            <thead>
                <tr className="text-left border-t border-white/10">
                  {/* Feature column header with billing toggle */}
                  <th className="hidden lg:table-cell p-6 bg-transparent w-1/4 align-bottom">
                    {/* Billing toggle in bottom left - with savings text below */}
                    <div className="flex flex-col items-start gap-2">
                      <div className="bg-[#202020] bg-opacity-50 rounded-full p-0.5 flex">
                        <button
                          onClick={() => setPeriodValue('monthly')}
                          className={`py-2 px-6 rounded-full transition-all text-sm ${
                            period === 'monthly'
                              ? 'bg-[#2563EB] text-white font-medium'
                              : 'text-gray-400'
                          }`}
                        >
                          {uiText.period_labels.monthly}
                        </button>
                      <button 
                          onClick={() => setPeriodValue('annual')}
                          className={`py-2 px-6 rounded-full transition-all text-sm ${
                            period === 'annual'
                              ? 'bg-[#2563EB] text-white font-medium'
                              : 'text-gray-400'
                          }`}
                      >
                        {uiText.period_labels.annual}
                      </button>
                    </div>
                      
                      {/* Savings text moved below */}
                      <p className="text-xs text-green-400">
                        {uiText.savings_text}
                      </p>
                  </div>
                </th>
                  
                  {/* Plan column headers */}
                {plans.map((plan) => (
                    <th 
                      key={plan.name} 
                      className={`p-4 lg:p-6 text-center ${plan.name === activePlan ? 'bg-blue-900 bg-opacity-50 ring-inset ring-2 ring-[#2563EB]' : 'bg-transparent'} w-1/5 cursor-pointer hover:bg-white/5 transition-colors`}
                      onClick={() => handleSelectPlan(plan.name)}
                    >
                      <h3 className="text-base lg:text-xl mb-2 truncate">{plan.display_name}</h3>
                      <div className="text-base lg:text-2xl font-bold mb-2 lg:mb-4">
                        {formatPrice(
                          plan.name, 
                          period === 'annual' ? plan.annual_price : plan.monthly_price
                        )}
                        {plan.name !== 'Enterprise' && (
                          <span className="text-xs lg:text-sm font-normal text-gray-400">/mo</span>
                        )}
                    </div>
                    <button 
                        className={`py-1 px-3 lg:px-4 text-white font-bold rounded transition-colors ${plan.name === activePlan ? 'bg-[#2563EB] hover:bg-blue-800' : 'bg-[#2563EB] hover:bg-blue-700'} text-xs lg:text-base`}
                        onClick={(e) => handleSelectButtonClick(e, plan)}
                      >
                        {plan.button_text}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            </table>
          </div>
        </div>
      </div>
      
      {/* Full-screen Sticky Header - Updated with Glass Effect */}
      <div 
        className={`fixed left-0 right-0 z-30 transition-all duration-300 ${
          showStickyHeader ? 'opacity-100 transform translate-y-0' : 'opacity-0 pointer-events-none transform -translate-y-8'
        }`}
        style={{ 
          height: showStickyHeader ? 'auto' : 0,
          top: 'calc(var(--header-height, 32px) + 4px)' // Added extra spacing to prevent chopping
        }}
      >
        <div className="bg-[#181818] bg-opacity-30 backdrop-blur-md shadow-lg border border-white/10 rounded-b-md">
          <div className="max-w-7xl mx-auto px-0 lg:px-8">
            {/* Sticky Header Content */}
            <div className="bg-transparent rounded-b-md">
              {/* Plan Selection Table - Simplified for sticky header */}
              <div className="w-full overflow-x-auto">
                <table className="w-full table-fixed border-collapse">
              <thead>
                    <tr className="text-left">
                      {/* Feature column header - hidden on mobile */}
                      <th className="hidden lg:table-cell p-4 bg-transparent w-1/4">
                        {/* Toggle in sticky header for desktop - updated to match image */}
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col items-start gap-1">
                            <div className="bg-[#202020] bg-opacity-50 rounded-full p-0.5 flex">
                              <button
                                onClick={() => setPeriodValue('monthly')}
                                className={`py-1 px-4 rounded-full transition-all text-xs ${
                                  period === 'monthly'
                                    ? 'bg-[#2563EB] text-white font-medium'
                                    : 'text-gray-400'
                                }`}
                              >
                                {uiText.period_labels.monthly}
                              </button>
                              <button
                                onClick={() => setPeriodValue('annual')}
                                className={`py-1 px-4 rounded-full transition-all text-xs ${
                                  period === 'annual'
                                    ? 'bg-[#2563EB] text-white font-medium'
                                    : 'text-gray-400'
                                }`}
                              >
                                {uiText.period_labels.annual}
                              </button>
                            </div>
                            <p className="text-xs text-green-400">
                              {uiText.savings_text}
                            </p>
                          </div>
                        </div>
                      </th>
                      
                      {/* Plan column headers for sticky header - reduced padding for mobile */}
                  {plans.map((plan) => (
                    <th 
                          key={`sticky-${plan.name}`} 
                          className={`p-2 lg:p-3 text-center ${plan.name === activePlan ? 'bg-blue-900 bg-opacity-50 ring-inset ring-1 ring-[#2563EB]' : 'bg-transparent'} w-1/5 cursor-pointer hover:bg-white/5 transition-colors`}
                          onClick={() => handleSelectPlan(plan.name)}
                        >
                          <div className="flex flex-col lg:flex-col items-center justify-between gap-1 lg:gap-1">
                            <h3 className="text-sm lg:text-xl truncate">{plan.display_name}</h3>
                            <div className="text-sm lg:text-lg font-bold">
                              {formatPrice(
                                plan.name,
                                period === 'annual' ? plan.annual_price : plan.monthly_price
                              )}
                              {plan.name !== 'Enterprise' && (
                                <span className="text-[10px] lg:text-sm font-normal text-gray-400">/mo</span>
                              )}
                      </div>
                      <button 
                              className={`py-0.5 lg:py-1 px-1.5 lg:px-2 text-white font-bold rounded transition-colors ${plan.name === activePlan ? 'bg-[#2563EB] hover:bg-blue-800' : 'bg-[#2563EB] hover:bg-blue-700'} text-[10px] lg:text-xs`}
                              onClick={(e) => handleSelectButtonClick(e, plan)}
                            >
                              {plan.button_text}
                      </button>
                          </div>
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
              </div>
              
              {/* Mobile Toggle - Only visible in sticky header on mobile - updated with reduced height */}
              <div className="lg:hidden flex justify-center items-center py-1 bg-transparent border-t border-white/10">
                <div className="bg-[#202020] bg-opacity-50 rounded-full p-0.5 flex">
                  <button
                    onClick={() => setPeriodValue('monthly')}
                    className={`py-1 px-4 rounded-full transition-all text-[10px] ${
                      period === 'monthly'
                        ? 'bg-[#2563EB] text-white font-medium'
                        : 'text-gray-400'
                    }`}
                  >
                    {uiText.period_labels.monthly}
                  </button>
                  <button
                    onClick={() => setPeriodValue('annual')}
                    className={`py-1 px-4 rounded-full transition-all text-[10px] ${
                      period === 'annual'
                        ? 'bg-[#2563EB] text-white font-medium'
                        : 'text-gray-400'
                    }`}
                  >
                    {uiText.period_labels.annual}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add style to detect and set header height */}
      <style jsx global>{`
        :root {
          --header-height: 36px; /* Slightly increased for mobile to prevent cropping */
        }
        @media (min-width: 1024px) {
          :root {
            --header-height: 68px; /* Slightly increased for desktop */
          }
        }
      `}</style>
      
      {/* Spacer for sticky header - Dynamic height based on header size */}
      {isHeaderSticky && (
        <div style={{ height: `${headerHeight.current}px` }} className="w-full"></div>
      )}
      
      {/* Features Section - Grid layout with Glass Effect */}
      <div className="w-full mt-8 px-0.5 lg:px-0">
        {featureCategories.map((category) => (
          <div key={category.name} className="mb-8">
            {/* Category Header - Updated for Glass Effect */}
            <div className="bg-gradient-to-r from-blue-900/70 to-blue-800/70 backdrop-blur-sm p-4 rounded-t-md border-t border-l border-r border-white/10">
              <h3 className="text-white font-semibold text-base lg:text-lg">{category.name}</h3>
            </div>
            
            {/* Features Grid - Transforms between desktop table and mobile sections */}
            <div className="bg-[#181818] bg-opacity-30 backdrop-blur-md rounded-b-md border-b border-l border-r border-white/10 overflow-hidden">
              {/* Desktop View - Traditional Table with fixed layout */}
              <table className="hidden lg:table w-full table-fixed border-collapse">
                  <tbody>
                  {category.features.map((feature, index) => (
                    <tr 
                      key={feature.id}
                      className={`${index % 2 === 1 ? 'bg-white/5' : 'bg-transparent'} border-t border-white/10`}
                    >
                      <td className="p-4 text-left w-1/4 font-medium">{feature.name}</td>
                      {plans.map((plan) => {
                        const planName = plan.name as PlanName;
                        return (
                          <td 
                            key={`${feature.id}-${plan.name}`} 
                            className={`p-4 text-center w-1/5 ${plan.name === activePlan ? 'bg-blue-900 bg-opacity-50' : 'bg-transparent'} cursor-pointer hover:bg-white/5`}
                            onClick={() => handleSelectPlan(plan.name)}
                          >
                            {renderFeatureValue(feature.included[planName])}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  </tbody>
                </table>
              
              {/* Mobile View - Grid-based Features with consistent widths */}
              <div className="lg:hidden">
                {category.features.map((feature, featureIndex) => (
                  <div 
                    key={`mobile-${feature.id}`}
                    className={`${featureIndex % 2 === 1 ? 'bg-white/5' : 'bg-transparent'} border-t border-white/10`}
                  >
                    {/* Feature Name */}
                    <div className="p-3 font-medium text-sm">
                      {feature.name}
                    </div>
                    
                    {/* Feature Values - Fixed width columns */}
                    <div className="grid grid-cols-4 border-t border-white/10">
                      {plans.map((plan) => {
                        const planName = plan.name as PlanName;
                        return (
                          <div 
                            key={`mobile-${feature.id}-${plan.name}`} 
                            className={`flex items-center justify-center p-3 ${plan.name === activePlan ? 'bg-blue-900 bg-opacity-50' : 'bg-transparent'} cursor-pointer hover:bg-white/5`}
                            onClick={() => handleSelectPlan(plan.name)}
                            style={{ minWidth: '25%' }}
                          >
                            {renderFeatureValue(feature.included[planName], true)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Footer / End of section marker */}
      <div className="w-full py-8 mt-12">
        {/* This acts as a visual end to the pricing section */}
      </div>
      
      {/* Floating Currency Selector - Always visible on the right side */}
      <CurrencySelector 
        currentCurrency={currency}
        onChange={handleCurrencyChange}
      />
    </div>
  );
};

export default PricingPlan; 